import { useState } from 'react';
import type { ChangeEvent } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { Popover, Stack, useMantineTheme } from '@mantine/core';

import { PasswordInput } from '~/domains/design-system/TextInput';
import dt from '~/testing';

import { passwordRequirements, getPasswordMatchRequirement } from './utils';
import PasswordRequirement from './PasswordRequirement';

type PasswordStrengthPopoverProps = {
  passwordProps: {
    value: string;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
    checked?: boolean;
    error?: string;
    onFocus?: () => void;
    onBlur?: () => void;
  };
  confirmPasswordProps?: {
    value: string;
    error?: string;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
    onBlur?: () => void;
  };
  showPopover?: boolean; // Optional prop to toggle the Popover visibility
  showIncorrectRequirements?: boolean; // Optional prop to toggle showing incorrect requirements
  backgroundColor?: string;
  hasBorder?: boolean;
  autoFocus?: boolean;
  confirmPassword?: boolean;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
};

function PasswordStrengthPopover({
  passwordProps,
  confirmPasswordProps,
  showPopover = true,
  showIncorrectRequirements = true,
  backgroundColor,
  hasBorder = true,
  autoFocus = false,
  confirmPassword = false,
  onKeyDown,
}: PasswordStrengthPopoverProps) {
  const password = (passwordProps?.value as string) || '';
  const confirmPasswordValue = (confirmPasswordProps?.value as string) || '';
  const { t } = useTranslation('auth');
  const [popoverOpened, setPopoverOpened] = useState(false);
  const theme = useMantineTheme();

  const allRequirements = [...passwordRequirements];
  if (confirmPassword) {
    allRequirements.push(getPasswordMatchRequirement(password, confirmPasswordValue));
  }

  const PasswordContent = (
    <Stack spacing={theme.other.spacing._8} mt={theme.other.spacing._24}>
      {allRequirements.map((requirement) => {
        const isValid = requirement.re ? requirement.re.test(password) : requirement.isValid;
        if (!showIncorrectRequirements && !isValid) {
          return null;
        }
        return (
          <PasswordRequirement
            key={requirement.label}
            label={t(requirement.label)}
            isValid={isValid}
          />
        );
      })}
    </Stack>
  );

  const getPasswordInputStyles = (bgColor?: string) =>
    bgColor
      ? {
          input: {
            backgroundColor: bgColor,
          },
          '& .mantine-PasswordInput-rightSection': {
            backgroundColor: bgColor,
            paddingRight: theme.other.spacing._16,
          },
        }
      : {};

  if (showPopover) {
    return (
      <Popover opened={popoverOpened} position="bottom" width="target">
        <Popover.Target>
          <div style={{ width: '100%' }}>
            <PasswordInput
              onFocusCapture={() => setPopoverOpened(true)}
              onBlurCapture={() => setPopoverOpened(false)}
              label={t('signUp.password.label')}
              data-test-id={dt.components.shared.auth.passwordInput}
              errorProps={{
                'data-test-id': dt.components.shared.auth.passwordError,
              }}
              w="100%"
              hasBorder={hasBorder}
              {...passwordProps}
              autoFocus={autoFocus}
              onKeyDown={onKeyDown}
              sx={getPasswordInputStyles(backgroundColor)}
            />
          </div>
        </Popover.Target>
        <Popover.Dropdown bg="white">{PasswordContent}</Popover.Dropdown>
      </Popover>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      <PasswordInput
        label={t('signUp.password.label')}
        data-test-id={dt.components.shared.auth.passwordInput}
        errorProps={{
          'data-test-id': dt.components.shared.auth.passwordError,
        }}
        w="100%"
        hasBorder={hasBorder}
        autoFocus={autoFocus}
        {...passwordProps}
        onKeyDown={onKeyDown}
        sx={getPasswordInputStyles(backgroundColor)}
      />
      {confirmPassword && (
        <PasswordInput
          label={t('signUp.confirmPassword.label')}
          data-test-id={dt.components.shared.auth.confirmPasswordInput}
          w="100%"
          hasBorder={hasBorder}
          mt={theme.other.spacing._16}
          {...confirmPasswordProps}
          sx={getPasswordInputStyles(backgroundColor)}
        />
      )}
      {PasswordContent}
    </div>
  );
}

export default PasswordStrengthPopover;
